import React, { useEffect, useRef, useState } from "react";

// Pagefind's Component UI is written entirely to /pagefind/ at build time
// (see scripts/build-search-index.js) and loaded here as a plain ES module.
// It reads the page's <html lang> to localize its own UI strings, so no
// French/English translation prop is needed here.
//
// The pagefind-input/results elements populate themselves as soon as the
// module script upgrades them, which happens before React hydrates this
// page. React's hydration pass then wipes that content because it expected
// these elements to stay empty. Creating them imperatively after mount,
// outside anything React reconciles, avoids the clash entirely.
//
// The module script is created and inserted imperatively rather than as a
// static JSX <script> tag, so its `onload` handler can be attached before
// the load can possibly fire — this is what gates creating the
// pagefind-input/results elements. Custom elements are supposed to
// auto-upgrade once defined even if created beforehand, but that ordering
// isn't reliable everywhere (seen failing on iPadOS Safari on a first,
// uncached load) — waiting for the real load event removes the dependency
// on that behavior instead of relying on it. (A dynamic import() here would
// do the same thing more simply, but Gatsby's SSR webpack build tries to
// statically resolve it — this URL only exists at runtime, in `public/`.)
export default function SearchWidget({ instance = "site-search", placeholder }) {
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const script = document.createElement("script");
    script.type = "module";
    script.onload = () => {
      const container = containerRef.current;
      if (cancelled || !container) return;

      ["pagefind-input", "pagefind-summary", "pagefind-results"].forEach(
        (tagName) => {
          const el = document.createElement(tagName);
          el.setAttribute("instance", instance);
          container.appendChild(el);
        },
      );
      setReady(true);
    };
    script.src = "/pagefind/pagefind-component-ui.js";
    document.body.appendChild(script);

    return () => {
      cancelled = true;
      script.remove();
      if (containerRef.current) containerRef.current.innerHTML = "";
      setReady(false);
    };
  }, [instance]);

  return (
    <div>
      <link href="/pagefind/pagefind-component-ui.css" rel="stylesheet" />
      {/* Rendered server-side so the search box is visible immediately,
          rather than as a blank space while the widget above loads and
          takes over. Hidden as soon as it does. */}
      {!ready && (
        <input
          type="search"
          disabled
          placeholder={placeholder}
          css={{
            width: "100%",
            height: `36px`,
            fontSize: `16px`,
            padding: `0 12px`,
            borderRadius: `6px`,
            border: `1px solid #e0e0e0`,
            color: `#767676`,
          }}
        />
      )}
      <div ref={containerRef} />
    </div>
  );
}
